/**
 * Case Subtype Taxonomy — admin tab container.
 *
 * Mounted between Templates and Statutory Library in TaskTemplatesPanel.
 * Manages Practice Area → Case Subtype mappings per jurisdiction and acts
 * as a semi-manual taxonomy discovery surface. Its producer is idempotent,
 * so runs are safe to repeat.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  Squares2X2Icon,
  ClipboardDocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import CoverageOverview from './CoverageOverview';
import MappingReviewQueue from './MappingReviewQueue';
import FormBindingReviewQueue from './FormBindingReviewQueue';
import CanonicalBrowser from './CanonicalBrowser';
import DiscoverFromUrlModal from './DiscoverFromUrlModal';
import { useFirmScope } from '../../../hooks/useFirmScope';
import type { CaseSubtypeLifecycle } from './caseSubtypeTaxonomyApi';

type InnerTab = 'coverage' | 'mappings' | 'form_bindings' | 'canonicals';

/**
 * Cross-tab navigation payload — Coverage tab metric cards and gap
 * drilldowns pass one of these to jump the user to the correct queue
 * pre-filtered. `highlightId` scrolls that mapping/binding into view so the
 * target row is obvious.
 */
export interface TaxonomyNavTarget {
  tab: InnerTab;
  status?: CaseSubtypeLifecycle | 'all';
  canonicalKey?: string;
  driftOnly?: boolean;
  highlightId?: string;
}

interface InnerTabConfig {
  id: InnerTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const INNER_TABS: InnerTabConfig[] = [
  {
    id: 'coverage',
    label: 'Coverage',
    icon: ChartBarIcon,
    description: 'Pending, validated, and rejected counts with blocker summary.',
  },
  {
    id: 'mappings',
    label: 'Mappings',
    icon: ClipboardDocumentCheckIcon,
    description: 'Review pending court/state mappings from local label → canonical subtype.',
  },
  {
    id: 'form_bindings',
    label: 'Form Bindings',
    icon: DocumentMagnifyingGlassIcon,
    description: 'Review pending bindings of canonical subtypes to court forms.',
  },
  {
    id: 'canonicals',
    label: 'Canonicals',
    icon: Squares2X2Icon,
    description: 'Browse the validated canonical taxonomy by practice area.',
  },
];

const ACCENT = {
  bar: 'from-violet-500 via-indigo-500 to-sky-500',
  activeBg: 'bg-violet-50 dark:bg-violet-900/30',
  activeText: 'text-violet-700 dark:text-violet-300',
  activeRing: 'ring-violet-500/20',
  dot: 'bg-violet-500',
  icon: 'text-violet-600 dark:text-violet-400',
  button: 'bg-violet-600 hover:bg-violet-500 text-white',
  buttonGhost: 'border-violet-500 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/30',
};

const CaseSubtypeTaxonomyPanel: React.FC = () => {
  const [innerTab, setInnerTab] = useState<InnerTab>('coverage');
  const [showDiscover, setShowDiscover] = useState(false);
  const [stateFilter, setStateFilter] = useState<string>('');
  const [practiceAreaFilter, setPracticeAreaFilter] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Pre-applied filters pushed in by the Coverage tab's click-throughs.
  // Each queue reads its own slice and clears it on first render so manual
  // filter changes inside the queue don't fight with this initial seed.
  const [mappingInitialStatus, setMappingInitialStatus] = useState<CaseSubtypeLifecycle | 'all'>('pending');
  const [mappingInitialCanonical, setMappingInitialCanonical] = useState<string | undefined>(undefined);
  const [mappingHighlightId, setMappingHighlightId] = useState<string | undefined>(undefined);
  const [bindingInitialStatus, setBindingInitialStatus] = useState<CaseSubtypeLifecycle | 'all'>('pending');
  const [bindingDriftOnly, setBindingDriftOnly] = useState(false);
  const [bindingInitialCanonical, setBindingInitialCanonical] = useState<string | undefined>(undefined);
  const [bindingHighlightId, setBindingHighlightId] = useState<string | undefined>(undefined);

  const firmScope = useFirmScope();

  const navigateTo = useCallback((target: TaxonomyNavTarget) => {
    if (target.tab === 'mappings') {
      if (target.status !== undefined) setMappingInitialStatus(target.status);
      setMappingInitialCanonical(target.canonicalKey);
      setMappingHighlightId(target.highlightId);
    } else if (target.tab === 'form_bindings') {
      if (target.status !== undefined) setBindingInitialStatus(target.status);
      setBindingDriftOnly(target.driftOnly === true);
      setBindingInitialCanonical(target.canonicalKey);
      setBindingHighlightId(target.highlightId);
    }
    setInnerTab(target.tab);
  }, []);

  const stateOptions = useMemo(() => {
    if (firmScope.overrideAll) return null; // null -> show free-text
    return firmScope.licensedStates;
  }, [firmScope]);

  const paOptions = useMemo(() => {
    if (firmScope.overrideAll) return null;
    return firmScope.practiceAreas;
  }, [firmScope]);

  const handleDiscoveryComplete = () => {
    setShowDiscover(false);
    setRefreshKey((k) => k + 1);
  };

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden"
      style={{ animation: 'fadeSlideIn 300ms ease-out forwards' }}
    >
      {/* Accent gradient top bar */}
      <div className={`h-1 w-full bg-gradient-to-r ${ACCENT.bar}`} />

      {/* Header row: inner tab bar + actions */}
      <div className="flex flex-wrap items-center gap-3 px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-700/50">
        <div
          className="flex items-center gap-1 bg-gray-100/80 dark:bg-gray-900/50 rounded-xl p-1.5"
          role="tablist"
          aria-label="Case subtype taxonomy sections"
        >
          {INNER_TABS.map((t) => {
            const active = t.id === innerTab;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setInnerTab(t.id)}
                className={`relative flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-violet-500 ${
                  active
                    ? `bg-white dark:bg-gray-700 shadow-sm ring-1 ${ACCENT.activeRing} ${ACCENT.activeText}`
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
                aria-selected={active}
                role="tab"
              >
                <Icon className={`h-4 w-4 ${active ? ACCENT.icon : ''}`} />
                <span>{t.label}</span>
                {active && (
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${ACCENT.dot}`}
                    style={{ animation: 'pulseOnce 300ms ease-out forwards' }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowDiscover(true)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${ACCENT.button}`}
            title="Fetches a URL and proposes pending mappings."
          >
            <GlobeAltIcon className="h-4 w-4" />
            Discover from URL
          </button>
        </div>
      </div>

      {/* Filters row (state + practice area) */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 bg-gray-50/50 dark:bg-gray-900/20 border-b border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            State
          </label>
          {stateOptions && stateOptions.length > 0 ? (
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
            >
              <option value="">All licensed</option>
              {stateOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="CA"
              className="w-16 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Practice Area
          </label>
          {paOptions && paOptions.length > 0 ? (
            <select
              value={practiceAreaFilter}
              onChange={(e) => setPracticeAreaFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
            >
              <option value="">All firm areas</option>
              {paOptions.map((p) => (
                <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={practiceAreaFilter}
              onChange={(e) => setPracticeAreaFilter(e.target.value.trim())}
              placeholder="family_law"
              className="w-48 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          )}
        </div>
        <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <input
            type="checkbox"
            checked={firmScope.overrideAll}
            onChange={(e) => firmScope.setOverrideAll(e.target.checked)}
            className="rounded text-violet-600 focus:ring-violet-500"
          />
          Show all jurisdictions (platform admin)
        </label>
      </div>

      {/* Description strip */}
      <div className="px-5 py-2.5 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/50">
        {INNER_TABS.find((t) => t.id === innerTab)?.description}
      </div>

      {/* Content */}
      <div className="p-5">
        {innerTab === 'coverage' && (
          <CoverageOverview
            key={`cov-${refreshKey}`}
            stateCode={stateFilter}
            practiceArea={practiceAreaFilter}
            onNavigate={navigateTo}
          />
        )}
        {innerTab === 'mappings' && (
          <MappingReviewQueue
            key={`map-${refreshKey}`}
            stateCode={stateFilter}
            practiceArea={practiceAreaFilter}
            initialStatus={mappingInitialStatus}
            initialCanonicalKey={mappingInitialCanonical}
            highlightId={mappingHighlightId}
            onChanged={() => setRefreshKey((k) => k + 1)}
          />
        )}
        {innerTab === 'form_bindings' && (
          <FormBindingReviewQueue
            key={`fb-${refreshKey}`}
            stateCode={stateFilter}
            initialStatus={bindingInitialStatus}
            initialCanonicalKey={bindingInitialCanonical}
            driftOnly={bindingDriftOnly}
            highlightId={bindingHighlightId}
            onChanged={() => setRefreshKey((k) => k + 1)}
          />
        )}
        {innerTab === 'canonicals' && (
          <CanonicalBrowser
            key={`can-${refreshKey}`}
            stateCode={stateFilter}
            practiceArea={practiceAreaFilter}
          />
        )}
      </div>

      {showDiscover && (
        <DiscoverFromUrlModal
          onClose={() => setShowDiscover(false)}
          onCompleted={handleDiscoveryComplete}
          defaultStateCode={stateFilter}
          defaultPracticeArea={practiceAreaFilter}
          firmStates={firmScope.licensedStates}
          firmPracticeAreas={firmScope.practiceAreas}
        />
      )}
    </div>
  );
};

export default CaseSubtypeTaxonomyPanel;
